import { useState, useRef, useEffect } from 'react'
import { Loader2, MessageSquare } from 'lucide-react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/utils/cn'
import { getInitials, getAvatarColor } from '@/utils/avatarUtils'
import { formatRelativeTime, formatUserDisplayName } from '@/utils/formatters'
import { useComments, useAddComment } from '@/hooks/useComments'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { toast } from '@/stores/useToastStore'
import type { CommentRead } from '@/api/comments'

const COMMENT_MAX_LENGTH = 2000
const COMMENT_WARNING_THRESHOLD = 1800
const PAGE_SIZE = 50

// --- CommentComposer ---

function CommentComposer({
  planId,
  onCommentPosted,
}: {
  planId: string
  onCommentPosted: () => void
}) {
  const [content, setContent] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { userId, name } = useCurrentUser()
  const addComment = useAddComment(planId)

  const isFocused = content.length > 0
  const isOverWarning = content.length >= COMMENT_WARNING_THRESHOLD
  const canSubmit =
    content.trim().length > 0 && content.length <= COMMENT_MAX_LENGTH

  const handleSubmit = () => {
    if (!canSubmit || addComment.isPending) return
    addComment.mutate(
      { content: content.trim() },
      {
        onSuccess: () => {
          setContent('')
          onCommentPosted()
        },
        onError: (error: Error) => {
          toast.error(error.message)
        },
      }
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      handleSubmit()
    }
  }

  // Auto-expand textarea
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${textarea.scrollHeight}px`
    }
  }, [content])

  const avatarColor = userId ? getAvatarColor(userId) : getAvatarColor('?')
  const initials = getInitials(name ?? null, userId ?? null)

  return (
    <div className="flex gap-3">
      <Avatar className="h-9 w-9 shrink-0">
        <AvatarFallback
          className={cn(
            avatarColor.bg,
            avatarColor.text,
            'text-xs font-medium'
          )}
        >
          {initials}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 space-y-2">
        <Textarea
          ref={textareaRef}
          placeholder="Add a comment..."
          value={content}
          onChange={e => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          maxLength={COMMENT_MAX_LENGTH}
          rows={2}
          className="resize-none min-h-[60px]"
        />
        <div className="flex items-center justify-end gap-3">
          {isFocused && (
            <span
              className={cn(
                'text-xs text-muted-foreground',
                isOverWarning && 'text-red-500'
              )}
            >
              {content.length.toLocaleString()} /{' '}
              {COMMENT_MAX_LENGTH.toLocaleString()}
            </span>
          )}
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={!canSubmit || addComment.isPending}
            className=""
          >
            {addComment.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Post'
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

// --- CommentCard ---

function CommentCard({ comment }: { comment: CommentRead }) {
  const authorName = comment.author
    ? formatUserDisplayName({
        id: comment.author.id,
        display_name: comment.author.display_name,
      })
    : 'Unknown user'

  const authorId = comment.author?.id ?? comment.id
  const avatarColor = getAvatarColor(authorId)
  const initials = getInitials(comment.author?.display_name ?? null, authorName)

  return (
    <div className="flex gap-3">
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarFallback
          className={cn(
            avatarColor.bg,
            avatarColor.text,
            'text-xs font-medium'
          )}
        >
          {initials}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium truncate">{authorName}</span>
          <span className="text-xs text-muted-foreground">·</span>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {formatRelativeTime(comment.created_at)}
          </span>
        </div>
        <p className="text-sm whitespace-pre-wrap mt-0.5">{comment.content}</p>
      </div>
    </div>
  )
}

// --- CommentDeletedCard ---

function CommentDeletedCard() {
  return (
    <p className="text-sm italic text-muted-foreground pl-11">
      This comment has been removed.
    </p>
  )
}

// --- CommentsTab ---

export function CommentsTab({ planId }: { planId: string }) {
  const [loadedPages, setLoadedPages] = useState(1)
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest')

  const { data, isLoading } = useComments(planId, 0, loadedPages * PAGE_SIZE)

  const comments = data?.data ?? []
  const sortedComments = [...comments].sort((a, b) => {
    const diff =
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    return sortOrder === 'newest' ? diff : -diff
  })
  const hasMore = data?.pagination?.has_more ?? false
  const total = data?.pagination?.total ?? 0
  const remaining = Math.max(0, total - comments.length)

  const handleLoadMore = () => {
    setLoadedPages(prev => prev + 1)
  }

  const handleCommentPosted = () => {
    // Reset to first page so new comment is visible
    setLoadedPages(1)
  }

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="flex flex-col gap-6">
        {/* Composer */}
        <CommentComposer
          planId={planId}
          onCommentPosted={handleCommentPosted}
        />

        {/* Comments header bar */}
        {!isLoading && comments.length > 0 && (
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span className="font-medium">
              {total} {total === 1 ? 'Comment' : 'Comments'}
            </span>
            <button
              type="button"
              onClick={() =>
                setSortOrder(prev => (prev === 'newest' ? 'oldest' : 'newest'))
              }
              className="text-xs hover:text-foreground transition-colors cursor-pointer"
            >
              {sortOrder === 'newest' ? 'Newest first' : 'Oldest first'}
            </button>
          </div>
        )}

        {/* Comments list */}
        {isLoading ? (
          <>
            <hr className="border-border" />
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          </>
        ) : comments.length === 0 ? (
          <>
            <hr className="border-border" />
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <MessageSquare className="h-10 w-10 mb-3" />
              <p className="text-lg font-medium">No comments yet</p>
              <p className="text-sm mt-1">
                Be the first to share your thoughts on this plan.
              </p>
            </div>
          </>
        ) : (
          <div className="space-y-5">
            {sortedComments.map(comment =>
              comment.is_deleted ? (
                <CommentDeletedCard key={comment.id} />
              ) : (
                <CommentCard key={comment.id} comment={comment} />
              )
            )}
          </div>
        )}

        {/* Load more */}
        {hasMore && (
          <div className="flex justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={handleLoadMore}
              className="cursor-pointer"
            >
              Load {remaining} more comment{remaining !== 1 ? 's' : ''}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
